/**
 * How the map draws aircraft trails and what a trail point says (Diurnal parity 03-029, 03-030; docs/specs/tracker.md
 * §7): the trail opacity and weight the user sets (the Diurnal sliders, am:3677–3689: 0–100 %, default 60 %, and
 * 0.5–5, default 2), one trail highlighted at a time (am:5052–5112), and the details of a trail point (am:6798–6830,
 * 7051–7101): altitude, speed, heading, the tracker's event, and the time in UTC and in the project's time zone. The
 * Diurnal V2Track trip popup was empty (bug 03-4); here every point says at least its time.
 */
import type { TrailPoint } from './aircraft-layer.js';
import { formatOffset, utcToLocalMs } from './time-model.js';
import type { TrackerSourceId } from './tracker.js';

export interface TrailStyle {
  /** 0 to 1. */
  readonly opacity: number;
  /** Line weight, 0.5 to 5 (screen pixels, the Diurnal steps). */
  readonly weight: number;
}

export const DEFAULT_TRAIL_STYLE: TrailStyle = { opacity: 0.6, weight: 2 };
export const TRAIL_WEIGHT_MIN = 0.5;
export const TRAIL_WEIGHT_MAX = 5;

/** A stored or chosen trail style made safe: opacity clamped to 0–1, weight to 0.5–5 in steps of 0.5. */
export function normaliseTrailStyle(v: unknown): TrailStyle {
  if (!v || typeof v !== 'object') return DEFAULT_TRAIL_STYLE;
  const o = v as Record<string, unknown>;
  const opacity =
    typeof o.opacity === 'number' && Number.isFinite(o.opacity)
      ? Math.min(1, Math.max(0, Math.round(o.opacity * 100) / 100))
      : DEFAULT_TRAIL_STYLE.opacity;
  const weight =
    typeof o.weight === 'number' && Number.isFinite(o.weight)
      ? Math.min(TRAIL_WEIGHT_MAX, Math.max(TRAIL_WEIGHT_MIN, Math.round(o.weight * 2) / 2))
      : DEFAULT_TRAIL_STYLE.weight;
  return { opacity, weight };
}

/** The trail clicked: highlighted, or back to normal when it already was (one at a time). */
export const toggleTrailHighlight = (current: string | null, id: string): string | null =>
  current === id ? null : id;

/** Trails drawn with a dot at each point (the Diurnal tool: V2Track, Spider Tracks and WebSentinel, am:6798). */
export const TRAIL_DOT_SOURCES: ReadonlySet<TrackerSourceId> = new Set([
  'v2track',
  'spidertracks',
  'websentinel',
]);

export interface TrailPointRow {
  readonly label: string;
  readonly value: string;
}

const two = (n: number): string => String(n).padStart(2, '0');
const hms = (ms: number): string => {
  const d = new Date(ms);
  return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}:${two(d.getUTCSeconds())}`;
};
const ymd = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${two(d.getUTCMonth() + 1)}-${two(d.getUTCDate())}`;
};

/**
 * The details of one trail point, as rows of words and numbers with units: "Altitude 1,385 ft", "Speed 125 kt",
 * "Heading 161°", "Event Enroute", "Time 05:38:00 (UTC+8), 21:38:00 UTC 2026-10-16". A value the tracker did not send
 * is left out; the time is always there.
 */
export function trailPointRows(p: TrailPoint, offsetH: number): TrailPointRow[] {
  const rows: TrailPointRow[] = [];
  if (p.altFt !== null && p.altFt !== undefined && Number.isFinite(p.altFt))
    rows.push({ label: 'Altitude', value: `${Math.round(p.altFt).toLocaleString('en-AU')} ft` });
  if (p.speedKt !== null && p.speedKt !== undefined && Number.isFinite(p.speedKt))
    rows.push({ label: 'Speed', value: `${Math.round(p.speedKt)} kt` });
  if (p.headingDeg !== null && p.headingDeg !== undefined && Number.isFinite(p.headingDeg))
    rows.push({ label: 'Heading', value: `${Math.round(p.headingDeg) % 360}°` });
  if (p.event) rows.push({ label: 'Event', value: p.event });
  if (p.timeMs !== undefined && Number.isFinite(p.timeMs)) {
    const local = utcToLocalMs(p.timeMs, offsetH);
    rows.push({
      label: 'Time',
      value: `${hms(local)} (UTC${formatOffset(offsetH)}) ${ymd(local)}, ${hms(p.timeMs)} UTC`,
    });
  } else rows.push({ label: 'Time', value: 'Not sent by the tracker' });
  return rows;
}
